
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, User, Settings, BookOpen, TrendingUp, Users, Clock } from 'lucide-react';

const Dashboard = () => {
  const [activeRoom, setActiveRoom] = useState<string | null>(null);

  useEffect(() => {
    // Impede indexação da página
    const metaRobots = document.createElement('meta');
    metaRobots.name = 'robots';
    metaRobots.content = 'noindex, nofollow';
    document.head.appendChild(metaRobots);

    // Configura o título da página
    document.title = 'Dashboard - QuantMake IA';

    return () => {
      document.head.removeChild(metaRobots);
    };
  }, []);

  const liveRooms = [
    {
      id: '1',
      name: 'Sala Principal - Day Trade',
      teacher: 'Carlos Silva',
      status: 'live',
      participants: 847,
      market: 'Índice Futuro',
      time: '09:30 - 17:30'
    },
    {
      id: '2',
      name: 'Sala FoFo - Swing Trade',
      teacher: 'Marina Santos',
      status: 'live',
      participants: 432,
      market: 'Ações',
      time: '10:00 - 16:00'
    },
    {
      id: '3',
      name: 'Sala Mini Dólar',
      teacher: 'Roberto Oliveira',
      status: 'scheduled',
      participants: 0,
      market: 'Dólar Futuro',
      time: '14:00 - 18:00'
    }
  ];

  const recentAlerts = [
    { time: '10:45', message: 'PETR4 - Oportunidade de compra identificada', type: 'buy' },
    { time: '10:30', message: 'VALE3 - Alerta de venda ativado', type: 'sell' },
    { time: '10:15', message: 'Índice em momento de decisão', type: 'info' }
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header Dashboard */}
      <header className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-green-400 rounded-full flex items-center justify-center">
                <span className="text-slate-900 font-bold text-sm">TA</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Área de Membros</h1>
                <p className="text-slate-400 text-sm">Bem-vindo de volta!</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <Bell className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <Settings className="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
                <User className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Salas ao Vivo */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-white mb-2">Salas ao Vivo</h2>
              <p className="text-slate-400">Acesse as transmissões em tempo real dos nossos analistas</p>
            </div>

            <div className="space-y-4">
              {liveRooms.map((room) => (
                <Card key={room.id} className="bg-slate-800/50 border-slate-700 hover:bg-slate-800/70 transition-all duration-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-3 h-3 rounded-full ${room.status === 'live' ? 'bg-red-500 animate-pulse' : 'bg-slate-500'}`}></div>
                        <div>
                          <CardTitle className="text-white text-lg">{room.name}</CardTitle>
                          <p className="text-slate-400 text-sm">com {room.teacher}</p>
                        </div>
                      </div>
                      <Badge variant={room.status === 'live' ? 'destructive' : 'secondary'}>
                        {room.status === 'live' ? 'AO VIVO' : 'AGENDADO'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-4 text-sm text-slate-400">
                        <div className="flex items-center space-x-1">
                          <Users className="h-4 w-4" />
                          <span>{room.participants}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <TrendingUp className="h-4 w-4" />
                          <span>{room.market}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Clock className="h-4 w-4" />
                          <span>{room.time}</span>
                        </div>
                      </div>
                    </div>
                    <Button 
                      className={`w-full ${room.status === 'live' 
                        ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' 
                        : 'bg-slate-700 hover:bg-slate-600'
                      } text-white font-semibold`}
                      onClick={() => setActiveRoom(room.id)}
                    >
                      {room.status === 'live' ? 'ENTRAR NA SALA' : 'AGENDAR LEMBRETE'}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Sidebar com Alertas e Recursos */}
          <div className="space-y-6">
            {/* Alertas Recentes */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center space-x-2">
                  <Bell className="h-5 w-5 text-yellow-400" />
                  <span>Alertas Recentes</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {recentAlerts.map((alert, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 bg-slate-700/50 rounded-lg">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        alert.type === 'buy' ? 'bg-green-400' : 
                        alert.type === 'sell' ? 'bg-red-400' : 'bg-blue-400'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-white text-sm">{alert.message}</p>
                        <p className="text-slate-400 text-xs">{alert.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Recursos Rápidos */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Recursos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700">
                  <BookOpen className="h-4 w-4 mr-2" />
                  Material de Apoio
                </Button>
                <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700">
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Relatórios
                </Button>
                <Button variant="ghost" className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700">
                  <Users className="h-4 w-4 mr-2" />
                  Comunidade
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
